import React, { useEffect, useMemo, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Phone, MessageCircle, Mail, MapPin, Plus, Search, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { STAGE_LABELS, STAGES_ORDER } from "@/i18n/translations";
import { useI18n } from "@/contexts/I18nContext";
import { useSearchParams } from "react-router-dom";

const STAGE_COLORS = {
  nouveau: "bg-slate-100 border-slate-300",
  contacte: "bg-blue-50 border-blue-300",
  rdv: "bg-purple-50 border-purple-300",
  visite: "bg-cyan-50 border-cyan-300",
  offre: "bg-amber-50 border-amber-300",
  negociation: "bg-orange-50 border-orange-300",
  paiement: "bg-lime-50 border-lime-300",
  client: "bg-emerald-50 border-emerald-300",
  perdu: "bg-red-50 border-red-300",
};

const ACTIVITY_TYPES = ["call", "message", "email", "visit", "note"];
const SOURCES = ["website", "whatsapp", "facebook", "instagram", "linkedin", "reference"];

export default function Leads() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { lang, t } = useI18n();
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [openDetail, setOpenDetail] = useState(false);
  const [current, setCurrent] = useState(null);
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  const [newLead, setNewLead] = useState({ name: "", phone: "", email: "", source: "website", notes: "" });
  const [activity, setActivity] = useState({ type: "call", description: "" });

  const load = () => api.get("/leads").then((r) => setLeads(r.data));

  useEffect(() => { load(); }, []);

  const columns = useMemo(() => {
    const byStage = Object.fromEntries(STAGES_ORDER.map((s) => [s, []]));
    for (const l of leads) {
      if (q && !`${l.name} ${l.phone} ${l.email}`.toLowerCase().includes(q.toLowerCase())) continue;
      (byStage[l.stage] || byStage.nouveau).push(l);
    }
    return byStage;
  }, [leads, q]);

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const { draggableId, destination, source } = result;
    if (destination.droppableId === source.droppableId) return;
    const newStage = destination.droppableId;
    setLeads((prev) => prev.map((l) => (l.id === draggableId ? { ...l, stage: newStage } : l)));
    try {
      await api.put(`/leads/${draggableId}`, { stage: newStage });
      toast.success("Étape mise à jour");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      load();
    }
  };

  const openLead = async (id) => {
    const { data } = await api.get(`/leads/${id}`);
    setCurrent(data);
    setOpenDetail(true);
  };

  useEffect(() => {
    const leadId = searchParams.get("lead");
    if (!leadId) return;
    openLead(leadId).then(() => {
      const next = new URLSearchParams(searchParams);
      next.delete("lead");
      setSearchParams(next, { replace: true });
    }).catch(() => toast.error("Prospect introuvable"));
    // The URL is only an instruction to open the newly converted prospect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const createLead = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", newLead);
      toast.success("Prospect créé");
      setNewLeadOpen(false);
      setNewLead({ name: "", phone: "", email: "", source: "website", notes: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const addActivity = async () => {
    if (!activity.description || !current) return;
    try {
      const { data } = await api.post(`/leads/${current.id}/activities`, activity);
      setCurrent({ ...current, activities: data.activities });
      setActivity({ type: "call", description: "" });
      toast.success("Activité ajoutée");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const updateStage = async (stage) => {
    if (!current) return;
    try {
      await api.put(`/leads/${current.id}`, { stage });
      setCurrent({ ...current, stage });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-leads">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold">Pipeline commercial</div>
          <h1 className="mt-2 font-display text-4xl text-brand-navy tracking-tight">{t("admin.leads")}</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="pl-9 w-64" data-testid="leads-search" />
          </div>
          <Button onClick={() => setNewLeadOpen(true)} className="bg-brand-orange hover:bg-brand-orangeDark text-white" data-testid="new-lead-btn">
            <Plus className="h-4 w-4 mr-1" /> Nouveau prospect
          </Button>
        </div>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex gap-3 overflow-x-auto pb-4">
          {STAGES_ORDER.map((s) => (
            <Droppable droppableId={s} key={s}>
              {(provided, snap) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`min-w-[260px] w-[260px] flex-shrink-0 rounded-lg border ${STAGE_COLORS[s]} p-2 ${snap.isDraggingOver ? "ring-2 ring-brand-orange" : ""}`}
                  data-testid={`col-${s}`}
                >
                  <div className="flex items-center justify-between px-2 py-2">
                    <div className="text-[11px] uppercase tracking-widest text-slate-700 font-semibold">{STAGE_LABELS[lang][s]}</div>
                    <span className="text-xs font-bold text-slate-500">{columns[s].length}</span>
                  </div>
                  <div className="space-y-2 min-h-[100px]">
                    {columns[s].map((l, idx) => (
                      <Draggable draggableId={l.id} index={idx} key={l.id}>
                        {(prov) => (
                          <Card
                            ref={prov.innerRef}
                            {...prov.draggableProps}
                            {...prov.dragHandleProps}
                            onClick={() => openLead(l.id)}
                            className="p-3 cursor-pointer hover:shadow-md transition-shadow bg-white border-slate-200"
                            data-testid={`lead-card-${l.id}`}
                          >
                            <div className="text-sm font-semibold text-brand-navy truncate">{l.name}</div>
                            <div className="mt-1 text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                              {l.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{l.phone}</span>}
                              {l.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{l.city}</span>}
                            </div>
                            {l.value > 0 && (
                              <div className="mt-2 text-xs font-bold text-brand-orange">${l.value.toLocaleString()}</div>
                            )}
                            <div className="mt-2 text-[10px] uppercase tracking-widest text-slate-400">via {l.source}</div>
                          </Card>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                </div>
              )}
            </Droppable>
          ))}
        </div>
      </DragDropContext>

      {/* Lead detail sheet */}
      <Sheet open={openDetail} onOpenChange={setOpenDetail}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="lead-detail-sheet">
          {current && (
            <>
              <SheetHeader>
                <SheetTitle className="font-display text-2xl text-brand-navy">{current.name}</SheetTitle>
              </SheetHeader>
              <div className="mt-6 space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-slate-700"><Phone className="h-4 w-4 text-brand-orange" />{current.phone || "—"}</div>
                  <div className="flex items-center gap-2 text-slate-700"><MessageCircle className="h-4 w-4 text-brand-orange" />{current.whatsapp || "—"}</div>
                  <div className="flex items-center gap-2 text-slate-700 truncate"><Mail className="h-4 w-4 text-brand-orange" />{current.email || "—"}</div>
                  <div className="flex items-center gap-2 text-slate-700"><MapPin className="h-4 w-4 text-brand-orange" />{current.city || "—"}</div>
                </div>
                <div>
                  <Label>Étape</Label>
                  <Select value={current.stage} onValueChange={updateStage}>
                    <SelectTrigger data-testid="lead-stage-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STAGES_ORDER.map((s) => <SelectItem key={s} value={s}>{STAGE_LABELS[lang][s]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {current.notes && (
                  <div>
                    <Label>Notes</Label>
                    <div className="rounded bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700 whitespace-pre-line">{current.notes}</div>
                  </div>
                )}

                <div className="pt-4 border-t">
                  <h3 className="font-display text-lg text-brand-navy">Historique</h3>
                  <ul className="mt-3 space-y-2">
                    {(current.activities || []).slice().reverse().map((a, i) => (
                      <li key={i} className="rounded bg-white border border-slate-200 p-3">
                        <div className="text-[10px] uppercase tracking-widest text-brand-orange font-semibold">{a.type}</div>
                        <div className="mt-1 text-sm text-slate-700">{a.description}</div>
                        <div className="mt-1 text-[10px] text-slate-400">{new Date(a.date).toLocaleString()}</div>
                      </li>
                    ))}
                    {(current.activities || []).length === 0 && <li className="text-sm text-slate-500 py-4 text-center">Aucune activité.</li>}
                  </ul>

                  <div className="mt-4 space-y-2">
                    <div className="grid grid-cols-3 gap-2">
                      <Select value={activity.type} onValueChange={(v) => setActivity({ ...activity, type: v })}>
                        <SelectTrigger className="col-span-1" data-testid="activity-type"><SelectValue /></SelectTrigger>
                        <SelectContent>{ACTIVITY_TYPES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input className="col-span-2" placeholder="Description…" value={activity.description} onChange={(e) => setActivity({ ...activity, description: e.target.value })} data-testid="activity-input" />
                    </div>
                    <Button onClick={addActivity} className="w-full bg-brand-navy hover:bg-brand-navyDeep text-white" data-testid="activity-add">Ajouter l'activité</Button>
                  </div>
                </div>

                {current.visits?.length > 0 && (
                  <div className="pt-4 border-t">
                    <h3 className="font-display text-lg text-brand-navy">Visites ({current.visits.length})</h3>
                    <ul className="mt-2 space-y-1 text-sm text-slate-700">
                      {current.visits.map((v) => <li key={v.id}>{v.date} {v.time} — {v.status}</li>)}
                    </ul>
                  </div>
                )}

                {current.invoices?.length > 0 && (
                  <div className="pt-4 border-t">
                    <h3 className="font-display text-lg text-brand-navy">Documents ({current.invoices.length})</h3>
                    <ul className="mt-2 space-y-1 text-sm text-slate-700">
                      {current.invoices.map((inv) => <li key={inv.id}>{inv.number} — ${inv.total} — {inv.status}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* New lead dialog */}
      <Dialog open={newLeadOpen} onOpenChange={setNewLeadOpen}>
        <DialogContent data-testid="new-lead-dialog">
          <DialogHeader><DialogTitle>Nouveau prospect</DialogTitle></DialogHeader>
          <form onSubmit={createLead} className="space-y-4 py-2">
            <div><Label>Nom</Label><Input value={newLead.name} onChange={(e) => setNewLead({ ...newLead, name: e.target.value })} required data-testid="new-lead-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Téléphone</Label><Input value={newLead.phone} onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })} data-testid="new-lead-phone" /></div>
              <div><Label>Email</Label><Input type="email" value={newLead.email} onChange={(e) => setNewLead({ ...newLead, email: e.target.value })} data-testid="new-lead-email" /></div>
            </div>
            <div>
              <Label>Source</Label>
              <Select value={newLead.source} onValueChange={(v) => setNewLead({ ...newLead, source: v })}>
                <SelectTrigger data-testid="new-lead-source"><SelectValue /></SelectTrigger>
                <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label><Textarea value={newLead.notes} onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })} data-testid="new-lead-notes" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setNewLeadOpen(false)}>Annuler</Button>
              <Button type="submit" className="bg-brand-orange hover:bg-brand-orangeDark text-white" data-testid="new-lead-submit">Créer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
